import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  APIActions,
  Box,
  CustomDialog,
  CustomPagination,
  HtmlTrans,
  MUIDataTable,
  MUIDataTableColumnDef,
  SearchBar,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import DeleteButton from 'web-library/components/buttons/DeleteButton';
import { AdminDetailsResponseContentObject } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';

const useStyles = makeStyles()((theme) =>
({
  searchBar: {
    marginBottom: theme.spacing(4),
  },
  dataTable: {
    marginBottom: theme.spacing(8),
  },
  nricText: {
    color: theme.color.text.textTertiary,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const tableColumns = (
  data: AdminDetailsResponseContentObject[],
  setDeleteAdminDetails: React.Dispatch<
    React.SetStateAction<{
      name: string;
      uuid: string;
    }>
  >,
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
  t: TFunction<'translation'>,
  classes: Record<string,string>
): MUIDataTableColumnDef[] => [
    {
      name: 'adminName',
      label: t('admin->>UserGroups.AdminName'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '40%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">{data[dataIndex].name}</Typography>
            <Typography className={classes.nricText} variant="subtitle2">
              {data[dataIndex].username}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'email',
      label: t('admin->>UserGroups.Email'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '40%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].email}</Typography>
        ),
      },
    },
    {
      name: 'action',
      label: t('admin->>UserGroups.Action'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <DeleteButton
            deleteHandler={() => {
              setDeleteAdminDetails({
                name: data[dataIndex].name,
                uuid: data[dataIndex].userUuid,
              });

              setOpenDeleteModal(true);
            }}
            label={t('announcementsNotifications->>Listing.Delete')}
            typographyVariant="h5"
          />
        ),
      },
    },
  ];

const TableFooter = ({
  classes,
  tableData,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
}: {
  classes: Record<string,string>;
  tableData: AdminDetailsResponseContentObject[];
  pageNumber: number;
  pageSize: number;
  setPageNumber: React.Dispatch<number>;
  setPageSize: React.Dispatch<number>;
}) => (
  <Box className={classes.paginationContainer}>
    <CustomPagination
      dataLength={tableData.length}
      page={pageNumber}
      rowsPerPage={pageSize}
      rowsOptions={[5, 10, 20, 40, 50]}
      setPage={(currentPage: number) => setPageNumber(currentPage)}
      setRowsPerPage={(numberOfRows: number) => {
        setPageNumber(0);
        setPageSize(numberOfRows);
      }}
    />
  </Box>
);

interface CreateEditViewUserGroupProps {
  selectedAdminDetails: AdminDetailsResponseContentObject[];
  setSelectedAdminDetails: React.Dispatch<
    React.SetStateAction<AdminDetailsResponseContentObject[]>
  >;
  setSelectedAdminUuids: React.Dispatch<React.SetStateAction<string[]>>;
}

const ViewAdminAccountDataTable = ({
  selectedAdminDetails,
  setSelectedAdminDetails,
  setSelectedAdminUuids,
}: CreateEditViewUserGroupProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();

  const [deleteAdminDetails, setDeleteAdminDetails] = useState<{
    name: string;
    uuid: string;
  }>({
    name: '',
    uuid: '',
  });

  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [tableData, setTableData] = useState(selectedAdminDetails);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  useEffect(() => {
    setTableData(
      selectedAdminDetails.filter(
        (adminDetail) =>
          adminDetail.email.toLowerCase().includes(search.toLowerCase()) ||
          adminDetail.name.toLowerCase().includes(search.toLowerCase()) ||
          adminDetail.username.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [search, selectedAdminDetails]);

  const handleDeleteSelectedAdmin = () => {
    const filteredSelectedAdmin = selectedAdminDetails.filter(
      (selectedCandidate) => selectedCandidate.userUuid !== deleteAdminDetails.uuid
    );
    setSelectedAdminDetails(filteredSelectedAdmin);
    setSelectedAdminUuids(Object.values(filteredSelectedAdmin).map((row) => row.userUuid));
    handleCloseDeleteModal();
    snackbarHandler?.setGlobalSnackbar({
      targetSeverity: 'success',
      targetData: t('admin->>UserGroups.AdminRemovedSnackbarMessage', {
        adminName: deleteAdminDetails.name,
      }),
    });
  };

  return (
    <>
      <SearchBar
        placeholder={t('admin->>UserGroups.AddAdminSearchPlaceholder')}
        name="searchInput"
        ariaLabel="searchInput"
        defaultValue={search}
        className={classes.searchBar}
        onSearch={(searchStr: string) => {
          setPageNumber(0);
          setSearch(searchStr.trim());
        }}
      />
      <Box className={classes.dataTable}>
        <MUIDataTable
          columns={tableColumns(tableData, setDeleteAdminDetails, setOpenDeleteModal, t, classes)}
          data={tableData}
          emptyDataLabel={t('admin->>UserGroups.NoAdminAddedMessage')}
          options={{
            pagination: true,
            sort: true,
            selectToolbarPlacement: 'none',
            selectableRows: 'none',
            serverSide: false,
            page: pageNumber,
            rowsPerPage: pageSize,
            onChangePage: (currentPage: number) => setPageNumber(currentPage),
            onChangeRowsPerPage: (numberOfRows: number) => setPageSize(numberOfRows),
          }}
          components={{
            TableFooter: () =>
              TableFooter({
                classes,
                tableData,
                pageNumber,
                pageSize,
                setPageNumber,
                setPageSize,
              }),
          }}
          rowsPerPageOptions={[5, 10, 20, 40, 50]}
        />
      </Box>

      <CustomDialog
        open={openDeleteModal}
        title={t('admin->>UserGroups.RemoveAdminName', {
          adminName: deleteAdminDetails.name,
        })}
        message={
          <HtmlTrans
            i18nKey="admin->>UserGroups.RemoveAdminNameStatement"
            values={{
              adminName: deleteAdminDetails.name,
            }}
          />
        }
        action={
          <APIActions
            onClose={handleCloseDeleteModal}
            apiCall={handleDeleteSelectedAdmin}
            cancelString={t('admin->>UserGroups.Cancel')}
            apiString={t('admin->>UserGroups.Proceed')}
          />
        }
      />
    </>
  );
};

export default ViewAdminAccountDataTable;
